// Portfolio mode (FOUNDER-OS; 12): `/c/{slug}/portfolio` (Focus), `brief`, `thinking` and a
// company's drill-down (`companies/{companyId}`). Only in the founder's Founder OS company (the
// registry's `companyKinds`); ModeGate renders "not available" elsewhere. FOUNDER-OS phase 1:
// `chart`, `goals` and `issues` redirect to KPIs › Chart, Priorities and Issues.
import { createRoute, Outlet, redirect } from "@tanstack/react-router";
import { CompanyDrilldown } from "../features/portfolio/company-drilldown";
import { FocusScreen } from "../features/portfolio/focus-screen";
import { PortfolioBriefScreen } from "../features/portfolio/portfolio-brief";
import { PortfolioThinkingScreen } from "../features/portfolio/portfolio-thinking";
import { useCompany } from "../features/use-feature";
import { ModeGate } from "../shell/mode-gate";
import { companyRoute } from "./c.$slug";

export const portfolioRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "portfolio",
  component: () => (
    <ModeGate modeKey="portfolio">
      <Outlet />
    </ModeGate>
  ),
});

const screen = <const P extends string>(path: P, Screen: typeof FocusScreen) =>
  createRoute({
    getParentRoute: () => portfolioRoute,
    path,
    component: function PortfolioScreen() {
      const { api } = portfolioRoute.useRouteContext();
      return <Screen api={api} company={useCompany()} />;
    },
  });

/**
 * FOUNDER-OS phase 1 (design §3.2): Chart, Goals and Issues moved to KPIs (one place per
 * concept); the old URLs land there, the query kept.
 */
const moved = <const P extends string>(path: P, to: string) =>
  createRoute({
    getParentRoute: () => portfolioRoute,
    path,
    beforeLoad: ({ params, search }) => {
      throw redirect({
        to,
        params: { slug: (params as { slug: string }).slug },
        search: search as Record<string, unknown>,
        replace: true,
      } as never);
    },
  });

export const portfolioFocusRoute = screen("/", FocusScreen);
export const portfolioChartRoute = moved("chart", "/c/$slug/kpis/chart");
export const portfolioGoalsRoute = moved("goals", "/c/$slug/kpis/priorities");
export const portfolioIssuesRoute = moved("issues", "/c/$slug/kpis/issues");
export const portfolioBriefRoute = screen("brief", PortfolioBriefScreen);
export const portfolioThinkingRoute = screen("thinking", PortfolioThinkingScreen);

export const portfolioCompanyRoute = createRoute({
  getParentRoute: () => portfolioRoute,
  path: "companies/$companyId",
  component: function PortfolioCompany() {
    const { api } = portfolioRoute.useRouteContext();
    const { companyId } = portfolioCompanyRoute.useParams();
    // Keyed: another company starts on its Overview tab.
    return (
      <CompanyDrilldown key={companyId} api={api} company={useCompany()} companyId={companyId} />
    );
  },
});

export const portfolioRoutes = [
  portfolioFocusRoute,
  portfolioChartRoute,
  portfolioGoalsRoute,
  portfolioIssuesRoute,
  portfolioBriefRoute,
  portfolioThinkingRoute,
  portfolioCompanyRoute,
];
